import React, { useEffect, useState } from 'react';
import { adminListLeads, adminPatchLead, adminDeleteLead } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Trash2, Mail, Phone, Building2, Calendar, Eye, EyeOff, ChevronRight } from 'lucide-react';

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListLeads();
      setLeads(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleRead = async (lead) => {
    const upd = await adminPatchLead(lead.id, { read: !lead.read });
    setLeads((ls) => ls.map((l) => (l.id === upd.id ? upd : l)));
    if (selected?.id === upd.id) setSelected(upd);
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este lead?')) return;
    await adminDeleteLead(id);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    if (selected?.id === id) setSelected(null);
    toast({ title: 'Lead eliminado' });
  };

  const formatDate = (s) => {
    try { return new Date(s).toLocaleString('es-ES'); } catch { return s; }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-3">Captación Comercial</div>
        <h1 className="font-display text-4xl text-white font-light">Leads Recibidos</h1>
        <p className="text-white/50 mt-2 text-sm">{leads.length} leads total · {leads.filter((l) => !l.read).length} sin leer</p>
      </div>

      {loading ? (
        <div className="text-white/40">Cargando...</div>
      ) : leads.length === 0 ? (
        <div className="text-white/40 p-10 text-center border border-dashed border-white/10 rounded-2xl">No hay leads todavía.</div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-4">
          <div className="lg:col-span-5 space-y-2 max-h-[80vh] overflow-y-auto pr-2">
            {leads.map((l) => (
              <button
                key={l.id}
                onClick={() => { setSelected(l); if (!l.read) toggleRead(l); }}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selected?.id === l.id
                    ? 'bg-[#00e0c7]/[0.06] border-[#00e0c7]/30'
                    : 'bg-white/[0.02] border-white/8 hover:bg-white/[0.04]'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-white font-medium text-sm truncate">{l.name}</div>
                  {!l.read && <span className="px-2 py-0.5 rounded-full bg-[#00e0c7]/15 text-[#00e0c7] text-[9px] uppercase tracking-widest">Nuevo</span>}
                </div>
                <div className="text-white/50 text-xs truncate">{l.company}</div>
                <div className="text-white/30 text-[11px] mt-1">{formatDate(l.createdAt)}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-7">
            {selected ? (
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="font-display text-white text-2xl font-light">{selected.name}</h2>
                    <div className="text-white/50 text-sm">{selected.position || '—'}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => toggleRead(selected)} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white hover:bg-white/5" title={selected.read ? 'Marcar no leído' : 'Marcar leído'}>
                      {selected.read ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => remove(selected.id)} className="p-2 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  <InfoRow icon={Building2} label="Empresa" value={selected.company} />
                  <InfoRow icon={Mail} label="Email" value={selected.email} />
                  <InfoRow icon={Phone} label="Teléfono" value={selected.phone || '—'} />
                  <InfoRow icon={Calendar} label="Fecha" value={formatDate(selected.createdAt)} />
                </div>

                {selected.service && (
                  <div className="mb-4 p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                    <div className="text-[10px] uppercase tracking-widest text-[#00e0c7] mb-1">Servicio solicitado</div>
                    <div className="text-white text-sm">{selected.service}</div>
                  </div>
                )}
                {selected.volume && (
                  <div className="mb-4 p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                    <div className="text-[10px] uppercase tracking-widest text-[#00e0c7] mb-1">Volumen estimado</div>
                    <div className="text-white text-sm">{selected.volume}</div>
                  </div>
                )}
                {selected.message && (
                  <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
                    <div className="text-[10px] uppercase tracking-widest text-[#00e0c7] mb-2">Mensaje</div>
                    <div className="text-white/80 text-sm whitespace-pre-wrap leading-relaxed">{selected.message}</div>
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-white/8 flex gap-2">
                  <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#00e0c7] text-[#0d1b2a] font-semibold text-sm">
                    Responder por email <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-white/40 p-10 text-center border border-dashed border-white/10 rounded-2xl">
                Selecciona un lead para ver el detalle.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]">
    <Icon className="w-4 h-4 text-[#00e0c7] mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-white text-sm truncate">{value}</div>
    </div>
  </div>
);

export default AdminLeads;
